import { colors } from '@globals/themes/defaultTheme'
import { keyframes, SxProps } from '@mui/material/styles'
import { T } from '@transifex/react'

const MESSAGE_INTERVAL_MS = 1800

export const loadingPhrases = [
  <T key="phrase1" _str="Polishing up that praise..." />,
  <T key="phrase2" _str="Adding a little extra sparkle..." />,
  <T key="phrase3" _str="Making your kind words shine..." />,
]

const CYCLE_MS = loadingPhrases.length * MESSAGE_INTERVAL_MS
const FADE_MS = 135

const percent = (value: number) => {
  const rounded = Math.round(value * 2) / 2
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1)
}

const fadePercent = (FADE_MS / CYCLE_MS) * 100
const slotPercent = (MESSAGE_INTERVAL_MS / CYCLE_MS) * 100

export const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)'

export const pulsingIconSx: SxProps = {
  animation: `${keyframes`
    0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.85; }
    50% { transform: scale(1.15) rotate(12deg); opacity: 1; }
  `} 1.1s ease-in-out infinite`,
  [REDUCED_MOTION]: { animation: 'none' },
}

const overlappingTextSx: SxProps = {
  gridArea: '1 / 1',
  whiteSpace: 'nowrap',
  textAlign: 'left',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

const exitAnimation = keyframes`
  from { opacity: 1; transform: translateY(0); filter: blur(0); }
  to { opacity: 0; transform: translateY(4px); filter: blur(0.5px); }
`

export const refineLabelSx = ({
  isPending,
  isHighlighted,
}: {
  isPending: boolean
  isHighlighted: boolean
}): SxProps => ({
  ...overlappingTextSx,
  ...(isPending && {
    animation: `${exitAnimation} 200ms ease-in forwards`,
    [REDUCED_MOTION]: { animation: 'none', visibility: 'hidden' },
  }),
  ...(isHighlighted && { color: colors.irisPrimary }),
})

// Each phrase is delayed by 1.8s. It fades in, holds, then fades out before the next
// one starts, so the label is briefly blank between phrases. Three phrases make a
// 5.4s loop: invisible until 2.5%, visible from 5% to 31%, gone by 33.5%.
const phraseCycle = keyframes`
  0%, ${percent(fadePercent)}% { opacity: 0; transform: translateY(4px); filter: blur(0.5px); animation-timing-function: ease-out; }
  ${percent(fadePercent * 2)}%, ${percent(slotPercent - fadePercent)}% { opacity: 1; transform: translateY(0); filter: blur(0); animation-timing-function: ease-in; }
  ${percent(slotPercent)}%, 100% { opacity: 0; transform: translateY(4px); filter: blur(0.5px); }
`

export const phraseCycleSx = (index: number): SxProps => ({
  ...overlappingTextSx,
  opacity: 0,
  animation: `${phraseCycle} ${CYCLE_MS}ms linear infinite backwards`,
  animationDelay: `${index * MESSAGE_INTERVAL_MS}ms`,
  [REDUCED_MOTION]:
    index === 0
      ? { animation: 'none', opacity: 1, transform: 'none', filter: 'none' }
      : { animation: 'none', visibility: 'hidden' },
})
